import { TAItem, TASubLine } from './types';
import { COLORS } from './colorTokens';

export interface TASettingRow {
  label: string;
  type: 'normal' | 'check';
  inputType: 'number' | 'select' | 'none';
  inputOptions?: string[];
  defaultValue?: number | string;
  color?: string;
  colorLabel?: string;
  /** Override default enabled state for this check row. Takes priority over singleSelect logic. */
  defaultEnabled?: boolean;
  /** When set on a select row, renders a number input immediately after the dropdown on the same line. */
  secondaryInput?: { defaultValue: number };
}

export interface TAIndicatorConfig {
  key: string;
  displayName: string;
  hasPaneHeight: boolean;
  settings: TASettingRow[];
  /** Only one check row can be enabled at a time (radio behavior) */
  singleSelect?: boolean;
}

export const INDICATOR_CONFIGS: Record<string, TAIndicatorConfig> = {
  // TREND
  SMA: {
    key: 'SMA',
    displayName: 'SMA',
    hasPaneHeight: false,
    singleSelect: true,
    settings: [
      {
        label: 'SMA 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa1,
      },
      {
        label: 'SMA 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
      {
        label: 'SMA 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 50,
        color: COLORS.chartTa3,
      },
      {
        label: 'SMA 4',
        type: 'check',
        inputType: 'number',
        defaultValue: 100,
        color: COLORS.chartTa4,
      },
      {
        label: 'SMA 5',
        type: 'check',
        inputType: 'number',
        defaultValue: 200,
        color: COLORS.chartTa5,
      },
    ],
  },
  WMA: {
    key: 'WMA',
    displayName: 'WMA',
    hasPaneHeight: false,
    singleSelect: true,
    settings: [
      {
        label: 'WMA 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa1,
      },
      {
        label: 'WMA 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
      {
        label: 'WMA 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 50,
        color: COLORS.chartTa3,
      },
      {
        label: 'WMA 4',
        type: 'check',
        inputType: 'number',
        defaultValue: 100,
        color: COLORS.chartTa4,
      },
      {
        label: 'WMA 5',
        type: 'check',
        inputType: 'number',
        defaultValue: 200,
        color: COLORS.chartTa5,
      },
    ],
  },
  EMA: {
    key: 'EMA',
    displayName: 'EMA',
    hasPaneHeight: false,
    singleSelect: true,
    settings: [
      {
        label: 'EMA 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa1,
      },
      {
        label: 'EMA 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
      {
        label: 'EMA 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 50,
        color: COLORS.chartTa3,
      },
      {
        label: 'EMA 4',
        type: 'check',
        inputType: 'number',
        defaultValue: 100,
        color: COLORS.chartTa4,
      },
      {
        label: 'EMA 5',
        type: 'check',
        inputType: 'number',
        defaultValue: 200,
        color: COLORS.chartTa5,
      },
    ],
  },
  MEMA: {
    key: 'MEMA',
    displayName: 'MEMA',
    hasPaneHeight: false,
    singleSelect: true,
    settings: [
      {
        label: 'MEMA 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa1,
      },
      {
        label: 'MEMA 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
      {
        label: 'MEMA 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 50,
        color: COLORS.chartTa3,
      },
      {
        label: 'MEMA 4',
        type: 'check',
        inputType: 'number',
        defaultValue: 100,
        color: COLORS.chartTa4,
      },
      {
        label: 'MEMA 5',
        type: 'check',
        inputType: 'number',
        defaultValue: 200,
        color: COLORS.chartTa5,
      },
    ],
  },
  TVMA: {
    key: 'TVMA',
    displayName: 'TVMA',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
      },
    ],
  },
  ICH: {
    key: 'ICH',
    displayName: 'ICH',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Turning Line',
        type: 'check',
        inputType: 'number',
        defaultValue: 9,
        color: COLORS.chartTa5,
      },
      {
        label: 'Base Line',
        type: 'check',
        inputType: 'number',
        defaultValue: 26,
        color: COLORS.chartTa2,
      },
      {
        label: 'Span A',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa7,
      },
      {
        label: 'Span B',
        type: 'check',
        inputType: 'number',
        defaultValue: 52,
        color: COLORS.chartTa6,
      },
      {
        label: 'Lagging Span',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa4,
      },
      {
        label: 'Up Trend Cloud',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa8,
      },
      {
        label: 'Down Trend Cloud',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa8,
      },
    ],
  },
  MACD: {
    key: 'MACD',
    displayName: 'MACD',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Short Term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 12,
        color: COLORS.chartTa2,
      },
      {
        label: 'Long Term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 26,
      },
      {
        label: 'Signal',
        type: 'normal',
        inputType: 'number',
        defaultValue: 9,
        color: COLORS.chartTa8,
      },
      {
        label: 'MACD Histogram: Rise Above',
        type: 'normal',
        inputType: 'none',
        color: COLORS.textUpColor,
      },
      {
        label: 'MACD Histogram: Fall Below',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  LRT: {
    key: 'LRT',
    displayName: 'LRT',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  SAR: {
    key: 'SAR',
    displayName: 'SAR',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Starting At',
        type: 'normal',
        inputType: 'number',
        defaultValue: 0.02,
        color: COLORS.chartTa2,
      },
      {
        label: 'Increasing By',
        type: 'normal',
        inputType: 'number',
        defaultValue: 0.02,
      },
      {
        label: 'Max Acceleration',
        type: 'normal',
        inputType: 'number',
        defaultValue: 0.2,
      },
    ],
  },
  ADXR: {
    key: 'ADXR',
    displayName: 'ADX-R',
    hasPaneHeight: true,
    settings: [
      {
        label: 'DMI',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
      { label: 'ADX', type: 'normal', inputType: 'number', defaultValue: 6 },
    ],
  },
  DMI: {
    key: 'DMI',
    displayName: 'DMI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'DMI',
        type: 'check',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.textUpColor,
        colorLabel: '+DI',
      },
      {
        label: 'DMI',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa2,
        colorLabel: '-DI',
      },
      {
        label: 'ADX',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa1,
      },
    ],
  },
  BIAS: {
    key: 'BIAS',
    displayName: 'Bias',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  ALF: {
    key: 'ALF',
    displayName: 'ALF',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },

  // VOLATILITY
  BB: {
    key: 'BB',
    displayName: 'BB',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Period',
        type: 'check',
        inputType: 'number',
        defaultValue: 25,
        color: COLORS.chartTa8,
        defaultEnabled: true,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'select',
        inputOptions: ['1', '2', '3', '4', '5'],
        defaultValue: '1',
        color: COLORS.chartTa4,
        colorLabel: 'σ+',
        defaultEnabled: false,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: 'σ-',
        defaultEnabled: false,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'select',
        inputOptions: ['1', '2', '3', '4', '5'],
        defaultValue: '2',
        color: COLORS.chartTa2,
        colorLabel: 'σ+',
        defaultEnabled: true,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: 'σ-',
        defaultEnabled: true,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'select',
        inputOptions: ['1', '2', '3', '4', '5'],
        defaultValue: '3',
        color: COLORS.chartTa4,
        colorLabel: 'σ+',
        defaultEnabled: false,
      },
      {
        label: 'StdDev',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: 'σ-',
        defaultEnabled: false,
      },
    ],
  },
  VC: {
    key: 'VC',
    displayName: 'VC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  ATR: {
    key: 'ATR',
    displayName: 'ATR',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  SD: {
    key: 'SD',
    displayName: 'SD',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
    ],
  },
  VltyCC: {
    key: 'VltyCC',
    displayName: 'VltyCC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
      },
    ],
  },

  // OSCILLATOR
  KRI: {
    key: 'KRI',
    displayName: 'KRI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
      {
        label: 'Period 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 3,
        defaultEnabled: false,
        color: COLORS.chartTa1,
      },
      {
        label: 'Moving average',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['SMA', 'EMA', 'MEMA'],
      },
      {
        label: 'Coefficient',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['Close', 'Volume'],
        defaultValue: 'Close',
      },
    ],
  },
  PL: {
    key: 'PL',
    displayName: 'PL',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
    ],
  },
  CO: {
    key: 'CO',
    displayName: 'CO',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['EMA', 'SMA'],
        defaultValue: 'EMA',
        secondaryInput: { defaultValue: 10 },
        color: COLORS.chartTa2,
      },
      { label: 'Period', type: 'normal', inputType: 'number', defaultValue: 3 },
    ],
  },
  RCI: {
    key: 'RCI',
    displayName: 'RCI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  FKD: {
    key: 'FKD',
    displayName: 'FKD',
    hasPaneHeight: true,
    settings: [
      {
        label: '%K',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa5,
      },
      {
        label: '%D',
        type: 'normal',
        inputType: 'number',
        defaultValue: 3,
        color: COLORS.chartTa2,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 80,
      },
    ],
  },
  SKD: {
    key: 'SKD',
    displayName: 'SKD',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Moving average',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['SMA', 'EMA', 'MEMA'],
      },
      {
        label: '%K',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa5,
      },
      {
        label: '%D',
        type: 'normal',
        inputType: 'number',
        defaultValue: 3,
        color: COLORS.chartTa2,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 80,
      },
    ],
  },
  CCI: {
    key: 'CCI',
    displayName: 'CCI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  MI: {
    key: 'MI',
    displayName: 'MI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['EMA', 'SMA'],
        defaultValue: 'EMA',
        secondaryInput: { defaultValue: 25 },
        color: COLORS.chartTa2,
      },
      { label: 'Period', type: 'normal', inputType: 'number', defaultValue: 9 },
    ],
  },
  UO: {
    key: 'UO',
    displayName: 'UO',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 7,
        color: COLORS.chartTa2,
      },
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
      },
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 28,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 30,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 70,
      },
    ],
  },
  Vector: {
    key: 'Vector',
    displayName: 'Vector',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'check',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
      {
        label: 'Moving average',
        type: 'check',
        inputType: 'number',
        defaultValue: 9,
        color: COLORS.chartTa1,
      },
    ],
  },
  DPO: {
    key: 'DPO',
    displayName: 'DPO',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa2,
      },
    ],
  },
  WAD: {
    key: 'WAD',
    displayName: 'WAD',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  Trix: {
    key: 'Trix',
    displayName: 'Trix',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Trix period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 15,
        color: COLORS.chartTa2,
      },
      {
        label: 'Signal',
        type: 'normal',
        inputType: 'number',
        defaultValue: 9,
        color: COLORS.chartTa1,
      },
    ],
  },

  // VOLUME
  Volume: {
    key: 'Volume',
    displayName: 'Volume',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Up colour',
        type: 'normal',
        inputType: 'none',
        color: COLORS.textUpColor,
      },
      {
        label: 'Down colour',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  VE: {
    key: 'VE',
    displayName: 'VE',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa8,
      },
      {
        label: '%',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: '%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
    ],
  },
  VAP: {
    key: 'VAP',
    displayName: 'VAP',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Up colour',
        type: 'normal',
        inputType: 'none',
        color: COLORS.textUpColor,
      },
      {
        label: 'Down colour',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  VOSC: {
    key: 'VOSC',
    displayName: 'VOSC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Short term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa2,
      },
      {
        label: 'Long term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
      },
    ],
  },
  VROC: {
    key: 'VROC',
    displayName: 'VROC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
      },
    ],
  },
  ACD: {
    key: 'ACD',
    displayName: 'ACD',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  OBV: {
    key: 'OBV',
    displayName: 'OBV',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  EOM: {
    key: 'EOM',
    displayName: 'EOM',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['SMA', 'EMA'],
        defaultValue: 'SMA',
        secondaryInput: { defaultValue: 14 },
        color: COLORS.chartTa2,
      },
    ],
  },
  FI: {
    key: 'FI',
    displayName: 'FI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['EMA', 'SMA'],
        defaultValue: 'EMA',
        secondaryInput: { defaultValue: 13 },
        color: COLORS.chartTa2,
      },
    ],
  },
  MFI: {
    key: 'MFI',
    displayName: 'MFI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 20,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 80,
      },
    ],
  },
  NVI: {
    key: 'NVI',
    displayName: 'NVI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'NVI',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
      {
        label: 'Period 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 255,
        color: COLORS.chartTa1,
      },
      {
        label: 'Period 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 255,
        defaultEnabled: false,
        color: COLORS.chartTa3,
      },
    ],
  },
  VR: {
    key: 'VR',
    displayName: 'VR',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },

  // MOMENTUM
  ENV: {
    key: 'ENV',
    displayName: 'ENV',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Period',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa8,
      },
      {
        label: '% 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 2.5,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: '% 1',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: '% 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 5,
        color: COLORS.chartTa2,
        colorLabel: '+P',
      },
      {
        label: '% 2',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: '% 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: '% 3',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: '% 4',
        type: 'check',
        inputType: 'number',
        defaultValue: 25,
        color: COLORS.chartTa2,
        colorLabel: '+P',
      },
      {
        label: '% 4',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: '% 5',
        type: 'check',
        inputType: 'number',
        defaultValue: 50,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: '% 5',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
    ],
  },
  MOM: {
    key: 'MOM',
    displayName: 'MOM',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
      },
    ],
  },
  RMI: {
    key: 'RMI',
    displayName: 'RMI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['MEMA', 'EMA', 'SMA'],
        defaultValue: 'MEMA',
        secondaryInput: { defaultValue: 14 },
        color: COLORS.chartTa2,
      },
      {
        label: 'Momentum period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 3,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 30,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 70,
      },
    ],
  },
  RSI: {
    key: 'RSI',
    displayName: 'RSI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period 1',
        type: 'check',
        inputType: 'select',
        inputOptions: ['EMA', 'SMA', 'MEMA'],
        defaultValue: 'EMA',
        secondaryInput: { defaultValue: 14 },
        color: COLORS.chartTa5,
      },
      {
        label: 'Period 2',
        type: 'check',
        inputType: 'select',
        inputOptions: ['EMA', 'SMA', 'MEMA'],
        defaultValue: 'EMA',
        defaultEnabled: false,
        secondaryInput: { defaultValue: 9 },
        color: COLORS.chartTa3,
      },
      {
        label: 'Oversold',
        type: 'normal',
        inputType: 'number',
        defaultValue: 30,
      },
      {
        label: 'Overbought',
        type: 'normal',
        inputType: 'number',
        defaultValue: 70,
      },
    ],
  },
  WR: {
    key: 'WR',
    displayName: 'WR',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },

  // PRICE
  POSC: {
    key: 'POSC',
    displayName: 'POSC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Short term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 12,
        color: COLORS.chartTa2,
      },
      {
        label: 'Long term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 26,
      },
    ],
  },
  PROC: {
    key: 'PROC',
    displayName: 'PROC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
  PRR: {
    key: 'PRR',
    displayName: 'PRR',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa8,
      },
    ],
  },
  VWAP: {
    key: 'VWAP',
    displayName: 'VWAP',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  WC: {
    key: 'WC',
    displayName: 'WC',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Weight x',
        type: 'normal',
        inputType: 'number',
        defaultValue: 2,
        color: COLORS.chartTa2,
      },
    ],
  },
  FIB: {
    key: 'FIB',
    displayName: 'FIB',
    hasPaneHeight: false,
    settings: [
      {
        label: '78.6%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
      {
        label: '61.8%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
      {
        label: '50.0%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa1,
      },
      {
        label: '38.2%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
      {
        label: '23.6%',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  PCV: {
    key: 'PCV',
    displayName: 'PCV',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Line',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
      },
    ],
  },
  RHI: {
    key: 'RHI',
    displayName: 'RHI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 26,
        color: COLORS.chartTa1,
        colorLabel: 'A',
      },
      {
        label: 'Period',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa2,
        colorLabel: 'B',
      },
    ],
  },

  // OTHER
  TP: {
    key: 'TP',
    displayName: 'TP',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa8,
      },
    ],
  },
  RC: {
    key: 'RC',
    displayName: 'RC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
      {
        label: 'Benchmark',
        type: 'normal',
        inputType: 'select',
        inputOptions: ['S&P500', 'ASX200', 'NASDAQ'],
      },
    ],
  },
  Turnover: {
    key: 'Turnover',
    displayName: 'Turnover',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Line colour',
        type: 'normal',
        inputType: 'none',
        color: COLORS.chartTa8,
      },
    ],
  },
  CC: {
    key: 'CC',
    displayName: 'CC',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'normal',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
      },
      {
        label: 'Short term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 11,
      },
      {
        label: 'Long term',
        type: 'normal',
        inputType: 'number',
        defaultValue: 14,
      },
    ],
  },
  KC: {
    key: 'KC',
    displayName: 'KC',
    hasPaneHeight: false,
    settings: [
      {
        label: 'Period',
        type: 'check',
        inputType: 'number',
        defaultValue: 20,
        color: COLORS.chartTa1,
      },
      {
        label: 'ATR period 1',
        type: 'check',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: 'ATR period 1',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: 'ATR period 2',
        type: 'check',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa2,
        colorLabel: '+P',
      },
      {
        label: 'ATR period 2',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
      {
        label: 'ATR period 3',
        type: 'check',
        inputType: 'number',
        defaultValue: 10,
        color: COLORS.chartTa4,
        colorLabel: '+P',
      },
      {
        label: 'ATR period 3',
        type: 'check',
        inputType: 'none',
        color: COLORS.chartTa5,
        colorLabel: '-P',
      },
    ],
  },
  UI: {
    key: 'UI',
    displayName: 'UI',
    hasPaneHeight: true,
    settings: [
      {
        label: 'Period',
        type: 'check',
        inputType: 'number',
        defaultValue: 14,
        color: COLORS.chartTa2,
      },
    ],
  },
};

/** Returns a compact period/param summary string for collapsed TA rows.
 *  Returns '' for indicators with no meaningful period. */
export function getDisplayPeriod(ta: TAItem): string {
  const { key, lines } = ta;
  const L = lines ?? [];

  switch (key) {
    // ── TREND ──────────────────────────────────────────────────────────────
    case 'SMA':
    case 'WMA':
    case 'EMA':
    case 'MEMA': {
      const enabled = L.filter((l) => l.enabled && l.period > 0).map(
        (l) => l.period,
      );
      return enabled.join(',') || '50';
    }
    case 'TVMA':
      return String(L[0]?.period ?? 10);
    case 'ICH': {
      const parts: number[] = [];
      if (L[0]?.enabled && L[0].period > 0) parts.push(L[0].period); // Turning
      if (L[1]?.enabled && L[1].period > 0) parts.push(L[1].period); // Base
      if (L[3]?.enabled && L[3].period > 0) parts.push(L[3].period); // Span B
      return parts.join(',') || '9,26,52';
    }
    case 'MACD': {
      const s = (L[0]?.period as unknown) ?? 12;
      const l = (L[1]?.period as unknown) ?? 26;
      const sig = (L[2]?.period as unknown) ?? 9;
      return `${s},${l},${sig}`;
    }
    case 'LRT':
      return '';
    case 'SAR':
      return `AF:${L[0]?.period ?? 0.02},${L[1]?.period ?? 0.02} EP:${L[2]?.period ?? 0.2}`;
    case 'ADXR':
      return `${L[0]?.period ?? 14},${L[1]?.period ?? 6}`;
    case 'DMI': {
      const p = L[0]?.enabled ? L[0].period : 14;
      return String(p);
    }
    case 'BIAS':
      return String(L[0]?.period ?? 14);
    case 'ALF':
      return String(L[0]?.period ?? 14);

    // ── VOLATILITY ─────────────────────────────────────────────────────────
    case 'BB': {
      const period = L[0]?.period ?? 25;
      const sds: string[] = [];
      if (L[1]?.enabled) sds.push(`${String(L[1]?.period ?? '1')}σ`);
      if (L[3]?.enabled) sds.push(`${String(L[3]?.period ?? '2')}σ`);
      if (L[5]?.enabled) sds.push(`${String(L[5]?.period ?? '3')}σ`);
      return sds.length > 0 ? `${period},${sds.join(',')}` : String(period);
    }
    case 'VC':
      return String(L[0]?.period ?? 14);
    case 'ATR':
      return String(L[0]?.period ?? 14);
    case 'SD':
      return String(L[0]?.period ?? 20);
    case 'VltyCC':
      return String(L[0]?.period ?? 10);

    // ── OSCILLATOR ─────────────────────────────────────────────────────────
    case 'KRI': {
      const parts: string[] = [];
      if (L[0]?.enabled && L[0].period > 0) parts.push(String(L[0].period));
      if (L[1]?.enabled && L[1].period > 0) parts.push(String(L[1].period));
      return parts.join(',') || '14,3';
    }
    case 'PL':
      return String(L[0]?.period ?? 20);
    case 'CO': {
      const cfg = INDICATOR_CONFIGS['CO'].settings;
      return `${cfg[0].defaultValue ?? 'EMA'}(${cfg[0].secondaryInput?.defaultValue ?? 10},${cfg[1].defaultValue ?? 3})`;
    }
    case 'RCI':
      return String(L[0]?.period ?? 14);
    case 'FKD': {
      const k = (L[0]?.period as unknown) ?? 14;
      const d = (L[1]?.period as unknown) ?? 3;
      return `${k},${d}`;
    }
    case 'SKD': {
      const ma =
        INDICATOR_CONFIGS['SKD'].settings[0].inputOptions?.[0] ?? 'SMA';
      const k = (L[1]?.period as unknown) ?? 14;
      const d = (L[2]?.period as unknown) ?? 3;
      return `${ma},${k},${d}`;
    }
    case 'CCI':
      return String(L[0]?.period ?? 14);
    case 'MI': {
      const cfg = INDICATOR_CONFIGS['MI'].settings;
      return `${cfg[0].defaultValue ?? 'EMA'}(${cfg[0].secondaryInput?.defaultValue ?? 25},${cfg[1].defaultValue ?? 9})`;
    }
    case 'UO':
      return `${L[0]?.period ?? 7},${L[1]?.period ?? 14},${L[2]?.period ?? 28}`;
    case 'Vector': {
      const parts = L.filter((l) => l.enabled && l.period > 0).map((l) =>
        String(l.period),
      );
      return parts.join(',') || '14,9';
    }
    case 'DPO':
      return String(L[0]?.period ?? 20);
    case 'WAD':
      return '';
    case 'Trix': {
      const t = (L[0]?.period as unknown) ?? 15;
      const sig = (L[1]?.period as unknown) ?? 9;
      return `${t},${sig}`;
    }

    // ── VOLUME ─────────────────────────────────────────────────────────────
    case 'Volume':
      return '';
    case 'VE': {
      const p = (L[0]?.period as unknown) ?? 20;
      const pct = (L[1]?.period as unknown) ?? 10;
      return `${p},${pct}`;
    }
    case 'VAP':
      return '';
    case 'VOSC':
      return `${L[0]?.period ?? 5},${L[1]?.period ?? 10}`;
    case 'VROC':
      return String(L[0]?.period ?? 10);
    case 'ACD':
      return '';
    case 'OBV':
      return '';
    case 'EOM': {
      const s0 = INDICATOR_CONFIGS['EOM'].settings[0];
      return `${s0.defaultValue ?? 'SMA'}(${s0.secondaryInput?.defaultValue ?? 14})`;
    }
    case 'FI': {
      const s0 = INDICATOR_CONFIGS['FI'].settings[0];
      return `${s0.defaultValue ?? 'EMA'}(${s0.secondaryInput?.defaultValue ?? 13})`;
    }
    case 'MFI':
      return String(L[0]?.period ?? 14);
    case 'NVI': {
      const parts = L.filter((l) => l.enabled && l.period > 0).map((l) =>
        String(l.period),
      );
      return parts.join(',') || '255,255';
    }
    case 'VR':
      return String(L[0]?.period ?? 14);

    // ── MOMENTUM ───────────────────────────────────────────────────────────
    case 'ENV': {
      const period = L[0]?.enabled ? String(L[0].period ?? 20) : '20';
      const pcts = [L[1], L[3], L[5], L[7], L[9]]
        .filter((l): l is TASubLine => !!l?.enabled && l.period > 0)
        .map((l) => String(l.period));
      return pcts.length > 0 ? `${period},${pcts.join(',')}` : period;
    }
    case 'MOM':
      return String(L[0]?.period ?? 10);
    case 'RMI': {
      const cfg = INDICATOR_CONFIGS['RMI'].settings;
      return `${cfg[0].defaultValue ?? 'MEMA'}(${cfg[0].secondaryInput?.defaultValue ?? 14},${cfg[1].defaultValue ?? 3})`;
    }
    case 'RSI': {
      // lines: [0]=P1, [1]=P2, [2]=Oversold, [3]=Overbought
      const parts: string[] = [];
      if (L[0]?.enabled !== false) parts.push(String(L[0]?.period ?? 14));
      if (L[1]?.enabled) parts.push(String(L[1]?.period ?? 9));
      return parts.join(',') || '14';
    }
    case 'WR':
      return String(L[0]?.period ?? 14);

    // ── PRICE ──────────────────────────────────────────────────────────────
    case 'POSC':
      return `${L[0]?.period ?? 12},${L[1]?.period ?? 26}`;
    case 'PROC':
      return String(L[0]?.period ?? 14);
    case 'PRR':
      return '';
    case 'VWAP':
      return '';
    case 'WC':
      return String(L[0]?.period ?? 2);
    case 'FIB':
      return '';
    case 'PCV':
      return '';
    case 'RHI':
      return String(L[0]?.period ?? 26);

    // ── OTHER ──────────────────────────────────────────────────────────────
    case 'TP':
      return String(L[0]?.period ?? 10);
    case 'RC':
      return String(L[0]?.period ?? 14);
    case 'Turnover':
      return '';
    case 'CC':
      return `${L[0]?.period ?? 10},${L[1]?.period ?? 11},${L[2]?.period ?? 14}`;
    case 'KC': {
      const period = L[0]?.enabled ? (L[0].period ?? 20) : 20;
      const atrs = [L[1], L[3], L[5]]
        .filter((l): l is TASubLine => !!l?.enabled && l.period > 0)
        .map((l) => String(l.period));
      return [String(period), ...atrs].join(',') || '20,10';
    }
    case 'UI': {
      const p = L[0]?.enabled ? L[0].period : 14;
      return String(p || 14);
    }

    default:
      return '';
  }
}

/** Returns the default period for a given indicator key.
 *  Reads the first normal-type number row's defaultValue; falls back to 14. */
export function getDefaultPeriod(key: string): number {
  const config = INDICATOR_CONFIGS[key];
  if (!config) return 14;
  const row = config.settings.find(
    (s) =>
      s.type === 'normal' &&
      s.inputType === 'number' &&
      s.defaultValue !== undefined,
  );
  return (row?.defaultValue as number) ?? 14;
}

/** Returns default sub-lines for check-type indicators (SMA, WMA, EMA, etc.).
 *  Returns undefined for indicators that don't use checkbox rows. */
export function getDefaultLines(key: string): TASubLine[] | undefined {
  const config = INDICATOR_CONFIGS[key];
  if (!config) return undefined;
  const checkRows = config.settings.filter((s) => s.type === 'check');
  if (checkRows.length > 0) {
    const lines: TASubLine[] = checkRows.map((row, idx) => ({
      enabled:
        row.defaultEnabled !== undefined
          ? row.defaultEnabled
          : config.singleSelect
            ? idx === 2
            : true,
      period: row.secondaryInput?.defaultValue ?? (row.defaultValue as number) ?? 14,
      color: row.color ?? COLORS.chartTa1,
    }));
    // Append all normal-type rows after the last check row (e.g. ICH cloud colors, RSI oversold/overbought)
    const lastCheckIdx = config.settings.reduce(
      (last, s, i) => (s.type === 'check' ? i : last),
      -1,
    );
    config.settings.forEach((s, i) => {
      if (i > lastCheckIdx && s.type === 'normal') {
        lines.push({
          enabled: true,
          period: s.secondaryInput
            ? s.secondaryInput.defaultValue
            : typeof s.defaultValue === 'number'
              ? s.defaultValue
              : 0,
          color: s.color ?? '',
        });
      }
    });
    return lines;
  }
  // Normal-type indicators: always return entries for every settings row so all
  // periods and colors can be tracked and updated by the user.
  // secondaryInput value is stored in lines[index].period for that select row.
  return config.settings.map((row) => ({
    enabled: true,
    period: row.secondaryInput
      ? row.secondaryInput.defaultValue
      : typeof row.defaultValue === 'number'
        ? row.defaultValue
        : 0,
    color: row.color ?? '',
  }));
}
